import React, { useEffect, useId, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styles from './VehicleManagementDrawer.module.css';
import { useModal } from '../../store/ModalProvider';
import type {
  DriverServiceTotalsModalData,
  DriversVehiclesAvailabilityModalData,
  OvertimeModalData,
  ActivityDeclarationsModalData,
  ChapasPorMotoristasModalData,
  ServicesByClientModalData,
  ServicesByLocalityModalData,
  SendScheduleModalData,
  TimesheetNoticesModalData,
  TimesheetMultipleDaysModalData,
  TachographServiceTypeModalData,
  VehicleServiceTotalsModalData,
  ItinerarySheetsModalData,
  ScheduleEmailsModalData,
} from '../../types/modal';

type VehicleManagementDrawerProps = {
  open: boolean;
  onClose: () => void;
};

type AccordionSectionProps = {
  id: string;
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
  headerContent?: React.ReactNode;
};

const AccordionSection = ({
  id,
  title,
  isOpen,
  onToggle,
  children,
  className,
  headerContent,
}: AccordionSectionProps) => (
  <div className={`${styles.section} ${className ?? ''}`.trim()}>
    <button
      type="button"
      className={`${styles.buttonReset} ${styles.sectionHeader}`}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`${id}-content`}
      id={`${id}-header`}
    >
      <span className={styles.sectionTitle}>{title}</span>
      <span className={styles.sectionHeaderRight}>
        {headerContent}
        <ExpandLessIcon
          className={`${styles.caret} ${isOpen ? styles.caretOpen : ''}`}
        />
      </span>
    </button>
    <div
      id={`${id}-content`}
      className={`${styles.sectionBody} ${isOpen ? styles.sectionBodyOpen : ''}`}
      role="region"
      aria-labelledby={`${id}-header`}
      aria-hidden={!isOpen}
    >
      {children}
    </div>
  </div>
);

type SelectFieldProps = {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
};

const SelectField = ({ label, value, options, onChange }: SelectFieldProps) => {
  const id = useId();

  return (
    <div className={styles.selectField}>
      <label className={styles.selectLabel} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className={styles.selectInput}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <KeyboardArrowDownIcon className={styles.selectIcon} />
    </div>
  );
};

export const VehicleManagementDrawer = ({
  open,
  onClose,
}: VehicleManagementDrawerProps) => {
  const { openModal } = useModal();
  const closeAnimationDurationMs = 400;
  const settingsStorageKey = 'vehicleManagementDrawerSettings';
  const defaultSettingsFlags = {
    alwaysUpdateAfterAssign: false,
    neverUpdate: false,
    rentalsAsRentPrimary: false,
    rentalsAsRentSecondary: false,
    markOvernightLocation: false,
    mailMapiUnsupported: false,
    sendCopyToDrivers: false,
    consultOtherOperators: false,
  };
  type SettingsFlags = typeof defaultSettingsFlags;
  const loadSettings = () => {
    if (typeof localStorage === 'undefined') {
      return {
        flags: defaultSettingsFlags,
        previewNextDayHours: '0 horas',
        defaultPrinter: '<Nome da Impressora>',
      };
    }

    const saved = localStorage.getItem(settingsStorageKey);
    if (!saved) {
      return {
        flags: defaultSettingsFlags,
        previewNextDayHours: '0 horas',
        defaultPrinter: '<Nome da Impressora>',
      };
    }

    try {
      const parsed = JSON.parse(saved);
      return {
        flags: {
          ...defaultSettingsFlags,
          ...(parsed?.flags ?? {}),
        },
        previewNextDayHours:
          typeof parsed?.previewNextDayHours === 'string'
            ? parsed.previewNextDayHours
            : '0 horas',
        defaultPrinter:
          typeof parsed?.defaultPrinter === 'string'
            ? parsed.defaultPrinter
            : '<Nome da Impressora>',
      };
    } catch (error) {
      console.error(
        'Failed to parse vehicle management settings from localStorage:',
        error,
      );
      return {
        flags: defaultSettingsFlags,
        previewNextDayHours: '0 horas',
        defaultPrinter: '<Nome da Impressora>',
      };
    }
  };

  const initialSettings = loadSettings();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const clientDefaultFilter = 'Todos';
  const requestDefaultFilter = 'Todos';
  const serviceTypeDefaultFilter = 'Todos';
  const loadServicesDefaultFilter = 'Todos';
  const capacityDefaultFilter = 'Todos';
  const serviceDateDefaultFilter = 'Todos do dia';
  const serviceOriginDefaultFilter = 'Todos';
  const serviceDestinationDefaultFilter = 'Todos';
  const driverDefaultFilter = 'Todos';
  const vehicleDefaultFilter = 'Todos';

  const clientOptions = ['Todos', 'Best Transfers 4 U', 'Scenic Tours'];
  const requestOptions = ['Todos', '95509', '84211', '84212'];
  const serviceTypeOptions = ['Todos', 'Agencia de Viagens', 'Ocasional'];
  const loadServicesOptions = ['Todos', 'Centro Atual', 'Todos os Centros'];
  const capacityOptions = ['Todos', '20', '40', '55'];
  const serviceDateOptions = ['Todos do dia', 'Hoje', 'Amanha'];
  const serviceOriginOptions = ['Todos', 'Lisboa', 'Porto', 'Faro'];
  const serviceDestinationOptions = ['Todos', 'Portimao', 'Braga', 'Coimbra'];
  const driverOptions = ['Todos', 'Alexandre Gomes', 'Beatriz Silva'];
  const vehicleOptions = ['Todos', '0046', '0062', '0129'];

  const [clientFilter, setClientFilter] = useState(clientDefaultFilter);
  const [requestFilter, setRequestFilter] = useState(requestDefaultFilter);
  const [serviceTypeFilter, setServiceTypeFilter] =
    useState(serviceTypeDefaultFilter);
  const [loadServicesFilter, setLoadServicesFilter] =
    useState(loadServicesDefaultFilter);
  const [capacityFilter, setCapacityFilter] = useState(capacityDefaultFilter);
  const [serviceDateFilter, setServiceDateFilter] =
    useState(serviceDateDefaultFilter);
  const [serviceOriginFilter, setServiceOriginFilter] =
    useState(serviceOriginDefaultFilter);
  const [serviceDestinationFilter, setServiceDestinationFilter] = useState(
    serviceDestinationDefaultFilter,
  );
  const [driverFilter, setDriverFilter] = useState(driverDefaultFilter);
  const [vehicleFilter, setVehicleFilter] = useState(vehicleDefaultFilter);

  const [applyDefaultFilters, setApplyDefaultFilters] = useState(true);
  const [filterWithNotes, setFilterWithNotes] = useState(true);
  const [filterWithoutDriver, setFilterWithoutDriver] = useState(true);
  const [filterWithoutVehicle, setFilterWithoutVehicle] = useState(true);
  const [filterWithAssignments, setFilterWithAssignments] = useState(true);
  const [settingsFlags, setSettingsFlags] = useState<SettingsFlags>(
    initialSettings.flags,
  );
  const [previewNextDayHours, setPreviewNextDayHours] = useState(
    initialSettings.previewNextDayHours,
  );
  const [defaultPrinter, setDefaultPrinter] = useState(
    initialSettings.defaultPrinter,
  );

  const filterSectionId = useId();
  const consultSectionId = useId();
  const sendSectionId = useId();
  const printSectionId = useId();
  const exportSectionId = useId();
  const settingsSectionId = useId();

  const driverServiceTotalsLabel = 'Serviços e Totais por Motorista';
  const vehicleServiceTotalsLabel = 'Serviços e Totais por Viatura';
  const servicesByClientLabel = 'Serviços por Cliente';
  const servicesByLocalityLabel = 'Serviços por localidade';
  const driversVehiclesAvailabilityLabel = 'Viaturas e Motoristas Disponíveis';
  const overtimeLabel = 'Horas Extra';
  const activityDeclarationsLabel = 'Declarações de Atividade';
  const timesheetNoticesLabel = 'Folhas de Ponto';
  const chapasPorMotoristasLabel = 'Chapas por Motoristas';

  const consultItems = [
    driverServiceTotalsLabel,
    vehicleServiceTotalsLabel,
    servicesByClientLabel,
    servicesByLocalityLabel,
    'Serviços por Tipo de Tacógrafo',
    driversVehiclesAvailabilityLabel,
    overtimeLabel,
  ];
  const sendItems = [
    'Escala',
    activityDeclarationsLabel,
    'Folhas de Itinerário',
    timesheetNoticesLabel,
    'Folhas de Ponto de vários dias',
    chapasPorMotoristasLabel,
  ];
  const printItems = [
    driverServiceTotalsLabel,
    'Serviços e Totais por Viatura',
    'Serviços por Cliente',
    'Horas Extra',
    'Escala',
    'Declarações de Atividade',
    'Folhas de Itinerário',
    'Folhas de Ponto',
    'Folhas de Ponto de vários dias',
    'Borrão',
    'Escalas de Viaturas',
    'Escalas de Expressos',
    'Escalas de Saída de Viaturas',
    'Escalas de Turismo',
    'Primeiro Troço de cada Chapa',
    'Lista de Motoristas para Escalar',
  ];
  const exportItems = [
    driverServiceTotalsLabel,
    vehicleServiceTotalsLabel,
    servicesByClientLabel,
    'Borrão',
    'Escalas de Viaturas',
    'Escalas de Expressos',
    'Escalas de Saída de Viaturas',
    'Escalas de Turismo',
    'Primeiro Troço de cada Chapa',
  ];
  const emailsScheduleLabel = 'Emails que recebem a escala';

  const filtersApplied = useMemo(() => {
    let count = 0;
    if (clientFilter !== clientDefaultFilter) {
      count += 1;
    }
    if (requestFilter !== requestDefaultFilter) {
      count += 1;
    }
    if (serviceTypeFilter !== serviceTypeDefaultFilter) {
      count += 1;
    }
    if (loadServicesFilter !== loadServicesDefaultFilter) {
      count += 1;
    }
    if (capacityFilter !== capacityDefaultFilter) {
      count += 1;
    }
    if (serviceDateFilter !== serviceDateDefaultFilter) {
      count += 1;
    }
    if (serviceOriginFilter !== serviceOriginDefaultFilter) {
      count += 1;
    }
    if (serviceDestinationFilter !== serviceDestinationDefaultFilter) {
      count += 1;
    }
    if (driverFilter !== driverDefaultFilter) {
      count += 1;
    }
    if (vehicleFilter !== vehicleDefaultFilter) {
      count += 1;
    }
    if (filterWithNotes) {
      count += 1;
    }
    if (filterWithoutDriver) {
      count += 1;
    }
    if (filterWithoutVehicle) {
      count += 1;
    }
    if (filterWithAssignments) {
      count += 1;
    }
    return count;
  }, [
    capacityDefaultFilter,
    capacityFilter,
    clientDefaultFilter,
    clientFilter,
    driverDefaultFilter,
    driverFilter,
    filterWithAssignments,
    filterWithNotes,
    filterWithoutDriver,
    filterWithoutVehicle,
    loadServicesDefaultFilter,
    loadServicesFilter,
    requestDefaultFilter,
    requestFilter,
    serviceDateDefaultFilter,
    serviceDateFilter,
    serviceDestinationDefaultFilter,
    serviceDestinationFilter,
    serviceOriginDefaultFilter,
    serviceOriginFilter,
    serviceTypeDefaultFilter,
    serviceTypeFilter,
    vehicleDefaultFilter,
    vehicleFilter,
  ]);

  const handleClearFilters = () => {
    setClientFilter(clientDefaultFilter);
    setRequestFilter(requestDefaultFilter);
    setServiceTypeFilter(serviceTypeDefaultFilter);
    setLoadServicesFilter(loadServicesDefaultFilter);
    setCapacityFilter(capacityDefaultFilter);
    setServiceDateFilter(serviceDateDefaultFilter);
    setServiceOriginFilter(serviceOriginDefaultFilter);
    setServiceDestinationFilter(serviceDestinationDefaultFilter);
    setDriverFilter(driverDefaultFilter);
    setVehicleFilter(vehicleDefaultFilter);
    setFilterWithNotes(false);
    setFilterWithoutDriver(false);
    setFilterWithoutVehicle(false);
    setFilterWithAssignments(false);
  };

  const settingsCheckboxItems = [
    {
      key: 'alwaysUpdateAfterAssign',
      label: 'Atualizar sempre após escalar um serviço',
    },
    {
      key: 'neverUpdate',
      label: 'Nunca Atualizar',
    },
    {
      key: 'rentalsAsRentPrimary',
      label:
        'Alugueres ocasionais, cliente passa a ser designado para “Aluguer”',
    },
    {
      key: 'rentalsAsRentSecondary',
      label:
        'Alugueres ocasionais, cliente passa a ser designado para “Aluguer”',
    },
    {
      key: 'markOvernightLocation',
      label:
        'Quando a viatura pernoitar fora do parque, no mapa das situações indicar esse local (em vez de “Parque)',
    },
    {
      key: 'mailMapiUnsupported',
      label: 'Este computador não suporta envio via Mail MAPI',
    },
    {
      key: 'sendCopyToDrivers',
      label: 'Enviar cópia das escalas aos motoristas com o meu conhecimento',
    },
    {
      key: 'consultOtherOperators',
      label: 'Consultar Escalas de outros operadores/filiais',
    },
  ] as const;

  const openDriverServiceTotalsModal = (
    footerVariant: DriverServiceTotalsModalData['footerVariant'] = 'export',
  ) => {
    const modalData: DriverServiceTotalsModalData = {
      driverId: 1,
      startDate: '02/05/2024',
      endDate: '08/05/2024',
      filial: 'Transol',
      driver: 'Alexandre Gomes',
      includeScheduleTime: true,
      footerVariant,
      driverCost: '0,00',
      services: [],
      totals: [],
      stats: {
        kmsCarga: '1890',
        kmsVazio: '246',
        totalValue: '930,90',
      },
    };

    openModal('Servicos e Totais por Motorista', modalData, []);
  };

  const openVehicleServiceTotalsModal = () => {
    const modalData: VehicleServiceTotalsModalData = {
      vehicleId: 1,
      startDate: '02/05/2024',
      endDate: '02/05/2024',
      filial: 'Transol',
      vehicle: '0003 - 78-XA-03',
      includeScheduleTime: true,
      footerVariant: 'export',
      services: [
        {
          id: '1',
          requisicao: '95509',
          motorista: 'Cavaquinho',
          numMec: '0118',
          dataInicio: '02/05/2024',
          horaInicio: '09:00',
          localInicio: 'Lisboa',
          localTermino: 'Torres Vedras',
          horaTermino: '10:00',
          dataTermino: '02/05/2024',
          cliente: 'Best Transfers 4 U',
        },
        {
          id: '2',
          requisicao: '95509',
          motorista: 'Cavaquinho',
          numMec: '0118',
          dataInicio: '02/05/2024',
          horaInicio: '09:00',
          localInicio: 'Lisboa',
          localTermino: 'Torres Vedras',
          horaTermino: '10:00',
          dataTermino: '02/05/2024',
          cliente: 'Best Transfers 4 U',
        },
      ],
      totals: [
        {
          id: '1',
          tipo: 'Agências Viagens',
          numero: '2',
          valor: '90,85',
          kmsCarga: '45',
          kmsVazio: '0',
          totalKms: '45',
        },
        {
          id: '2',
          tipo: 'Totais',
          numero: '2',
          valor: '90,85',
          kmsCarga: '45',
          kmsVazio: '0',
          totalKms: '45',
        },
      ],
      stats: {
        kmsCarga: '200',
        kmsVazio: '50',
        totalKms: '250',
        duracao: '02:00',
      },
    };

    openModal('Servicos e Totais por Viatura', modalData, []);
  };

  const openServicesByClientModal = () => {
    const modalData: ServicesByClientModalData = {
      startDate: '02/05/2024',
      endDate: '02/05/2024',
      cliente: 'Best Transfers 4 U',
      clientes: ['Best Transfers 4 U', 'Transol Corporate', 'Frota Azul'],
      rows: [
        {
          id: '1',
          dataInicio: '02/05/2024',
          horaInicio: '08:00',
          servico: 'Transfer',
          localInicio: 'Lisboa',
          localTermino: 'Faro',
          horaTermino: '11:00',
          dataTermino: '02/05/2024',
          cliente: 'Best Transfers 4 U',
          observacoes: 'Sem observações',
          motorista: 'João Oliveira',
          viatura: '0062',
        },
        {
          id: '2',
          dataInicio: '02/05/2024',
          horaInicio: '09:00',
          servico: 'Excursão',
          localInicio: 'Setúbal',
          localTermino: 'Sintra',
          horaTermino: '12:00',
          dataTermino: '02/05/2024',
          cliente: 'Transol Corporate',
          observacoes: 'Cliente VIP',
          motorista: 'Ana Martins',
          viatura: '0046',
        },
      ],
    };

    openModal('Servicos por Cliente', modalData, []);
  };

  const openServicesByLocalityModal = () => {
    const modalData: ServicesByLocalityModalData = {
      startDate: '02/05/2024',
      endDate: '02/05/2024',
      filial: 'Transol',
      filiais: ['Transol', 'Follow Me'],
      servico: 'Todos',
      servicos: ['Todos', 'Serviços Escalados'],
      localidadeFiltroPor: 'Origem / Destino comece por',
      localidadeFiltroPorOptions: ['Origem / Destino comece por'],
      localidade: 'Lisboa',
      localidades: ['Lisboa', 'Porto', 'Faro'],
      rows: [
        {
          id: '1',
          requisicao: '95509',
          dataInicio: '02/05/2024',
          horaInicio: '08:00',
          localInicio: 'Lisboa',
          localTermino: 'Faro',
          horaTermino: '11:00',
          dataTermino: '02/05/2024',
          motorista: 'João Oliveira',
          cliente: 'Best Transfers 4 U',
        },
        {
          id: '2',
          requisicao: '84211',
          dataInicio: '02/05/2024',
          horaInicio: '09:15',
          localInicio: 'Lisboa',
          localTermino: 'Sintra',
          horaTermino: '10:10',
          dataTermino: '02/05/2024',
          motorista: 'Ana Martins',
          cliente: 'Transol Corporate',
        },
        {
          id: '3',
          requisicao: '84212',
          dataInicio: '02/05/2024',
          horaInicio: '10:30',
          localInicio: 'Setúbal',
          localTermino: 'Lisboa',
          horaTermino: '11:30',
          dataTermino: '02/05/2024',
          motorista: 'Rui Silva',
          cliente: 'Scenic Tours',
        },
        {
          id: '4',
          requisicao: '84213',
          dataInicio: '02/05/2024',
          horaInicio: '12:00',
          localInicio: 'Lisboa',
          localTermino: 'Coimbra',
          horaTermino: '14:40',
          dataTermino: '02/05/2024',
          motorista: 'Beatriz Costa',
          cliente: 'Best Transfers 4 U',
        },
        {
          id: '5',
          requisicao: '84214',
          dataInicio: '02/05/2024',
          horaInicio: '14:30',
          localInicio: 'Porto',
          localTermino: 'Braga',
          horaTermino: '15:45',
          dataTermino: '02/05/2024',
          motorista: 'Carlos Sousa',
          cliente: 'Transol Corporate',
        },
        {
          id: '6',
          requisicao: '84215',
          dataInicio: '02/05/2024',
          horaInicio: '16:00',
          localInicio: 'Lisboa',
          localTermino: 'Évora',
          horaTermino: '18:10',
          dataTermino: '02/05/2024',
          motorista: 'Miguel Marques',
          cliente: 'Frota Azul',
        },
      ],
    };

    openModal('Serviços por Localidade', modalData, []);
  };

  const openDriversVehiclesAvailabilityModal = () => {
    const modalData: DriversVehiclesAvailabilityModalData = {
      startDate: '02/05/2024 13:15',
      endDate: '03/05/2024 16:15',
      empresa: 'Transol',
      filial: 'Transol',
      lotacaoOperador: 'Superior ou Igual a',
      lotacao: '40',
      anoViatura: 'Posterior a 2020',
      drivers: [
        {
          id: 'driver-1',
          motorista: 'Beatriz Silva',
          numMec: '298',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portimão',
          numFrota: '0062',
          telefone: '910000000',
          inicioPosterior: '02/05/2024 17:00',
          fim: '',
        },
        {
          id: 'driver-2',
          motorista: 'Carlos Cardoso',
          numMec: '123',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portimão',
          numFrota: '0062',
          telefone: '910000000',
          inicioPosterior: '02/05/2024 17:00',
          fim: '',
        },
      ],
      vehicles: [
        {
          id: 'vehicle-1',
          numFrota: '0062',
          lotacao: '55',
          ano: '2021',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portimão',
          numMec: '199',
          motorista: 'Anabela Ramos',
          telefone: '910000000',
          folga: '',
        },
        {
          id: 'vehicle-2',
          numFrota: '0021',
          lotacao: '40',
          ano: '2021',
          ultimoServicoInicio: '02/05/2024 15:00',
          ultimoServicoFim: '02/05/2024 16:00',
          origem: 'Faro',
          destino: 'Portimão',
          numMec: '232',
          motorista: 'Miguel Marques',
          telefone: '910000000',
          folga: '',
        },
      ],
    };

    openModal('Disponibilidade de Motoristas e Viaturas', modalData, []);
  };

  const openOvertimeModal = () => {
    const modalData: OvertimeModalData = {
      startDate: '02/05/2024',
      endDate: '02/05/2024',
      empresa: 'Transol',
      filial: 'Transol',
      motorista: 'Todos',
      motoristas: ['Todos', 'Alexandre Gomes', 'Ana Martins', 'Rui Silva'],
      rows: [
        {
          id: 'ot-1',
          motorista: 'Alexandre Gomes',
          nr: '0137',
          data: '02/05/2024',
          horasExtra: '02:30',
          servico: 'Transfer',
          observacoes: 'Atraso na saída',
          viatura: '0062',
          cliente: 'Best Transfers 4 U',
        },
        {
          id: 'ot-2',
          motorista: 'Ana Martins',
          nr: '0210',
          data: '02/05/2024',
          horasExtra: '01:45',
          servico: 'Excursão',
          observacoes: 'Cliente VIP',
          viatura: '0046',
          cliente: 'Transol Corporate',
        },
      ],
    };

    openModal('Horas Extra', modalData, []);
  };

  const openSendScheduleModal = () => {
    const modalData: SendScheduleModalData = {
      centroId: '1',
      date: '02/05/2024',
      scheduledServices: [],
    };

    openModal('Enviar Escala', modalData, []);
  };

  const openTimesheetNoticesModal = () => {
    const modalData: TimesheetNoticesModalData = {
      timesheets: [],
      lateWarnings: [],
    };

    openModal('Folhas de Ponto e Avisos de Folga Atrasada', modalData, []);
  };

  const openTimesheetMultipleDaysModal = () => {
    const modalData: TimesheetMultipleDaysModalData = {
      startDate: '02/05/2024',
      endDate: '08/05/2024',
      motorista: 'Todos',
      motoristas: [],
      includeItineraries: true,
      drivers: [],
      days: [],
    };

    openModal('Folhas de Ponto de vários dias', modalData, []);
  };

  const openChapasPorMotoristasModal = () => {
    const modalData: ChapasPorMotoristasModalData = {
      startDate: '01/11/2024',
      endDate: '30/11/2024',
      motorista: 'Anabela, Isabel, Rui (+2)',
      sazonalidade: 'Todas',
      chapa: '0003, 0016',
      motoristas: ['Anabela, Isabel, Rui (+2)', 'Todos'],
      sazonalidades: ['Todas', 'Alta', 'Baixa'],
      chapas: ['0003, 0016', '0003', '0016'],
      rows: [],
    };

    openModal('Chapas por Motoristas', modalData, []);
  };

  const openItinerarySheetsModal = () => {
    const modalData: ItinerarySheetsModalData = {
      date: '02/05/2024',
      includeExternal: true,
      includeInternal: true,
      rows: [],
    };

    openModal('Folhas de Itinerário', modalData, []);
  };

  const openActivityDeclarationsModal = (
    initialTab: ActivityDeclarationsModalData['initialTab'] = 'toIssue',
  ) => {
    const modalData: ActivityDeclarationsModalData = {
      toIssue: [],
      issued: [],
      initialTab,
    };

    openModal('Declaracoes de Atividade', modalData, []);
  };

  const handleConsultItemClick = (item: string) => {
    if (item === 'Serviços por Tipo de Tacógrafo') {
      const modalData: TachographServiceTypeModalData = {
        rows: [],
      };

      openModal('Serviços por Tipo de Tacógrafo', modalData, []);
      return;
    }

    if (item === vehicleServiceTotalsLabel) {
      openVehicleServiceTotalsModal();
      return;
    }

    if (item === driversVehiclesAvailabilityLabel) {
      openDriversVehiclesAvailabilityModal();
      return;
    }

    if (item === servicesByClientLabel) {
      openServicesByClientModal();
      return;
    }

    if (item === servicesByLocalityLabel) {
      openServicesByLocalityModal();
      return;
    }

    if (item === overtimeLabel) {
      openOvertimeModal();
      return;
    }

    if (item === driverServiceTotalsLabel) {
      openDriverServiceTotalsModal('export');
    }
  };

  const handleSendItemClick = (item: string) => {
    if (item === 'Escala') {
      openSendScheduleModal();
      return;
    }
    if (item === activityDeclarationsLabel) {
      openActivityDeclarationsModal('toIssue');
      return;
    }
    if (item === 'Folhas de Itinerário') {
      openItinerarySheetsModal();
      return;
    }
    if (item === timesheetNoticesLabel) {
      openTimesheetNoticesModal();
      return;
    }
    if (item === 'Folhas de Ponto de vários dias') {
      openTimesheetMultipleDaysModal();
      return;
    }
    if (item === chapasPorMotoristasLabel) {
      openChapasPorMotoristasModal();
    }
  };

  const handlePrintItemClick = (item: string) => {
    if (item === driverServiceTotalsLabel) {
      openDriverServiceTotalsModal('export');
      return;
    }
    if (item === vehicleServiceTotalsLabel) {
      openVehicleServiceTotalsModal();
      return;
    }
    if (item === servicesByClientLabel) {
      openServicesByClientModal();
      return;
    }
    if (item === overtimeLabel) {
      openOvertimeModal();
      return;
    }
    if (item === 'Escala') {
      openSendScheduleModal();
      return;
    }
    if (item === activityDeclarationsLabel) {
      openActivityDeclarationsModal('toIssue');
      return;
    }
    if (item === 'Folhas de Itinerário') {
      openItinerarySheetsModal();
      return;
    }
    if (item === timesheetNoticesLabel) {
      openTimesheetNoticesModal();
      return;
    }
    if (item === 'Folhas de Ponto de vários dias') {
      openTimesheetMultipleDaysModal();
    }
  };

  const handleExportItemClick = (item: string) => {
    if (item === driverServiceTotalsLabel) {
      openDriverServiceTotalsModal('export');
      return;
    }
    if (item === vehicleServiceTotalsLabel) {
      openVehicleServiceTotalsModal();
      return;
    }
    if (item === servicesByClientLabel) {
      openServicesByClientModal();
      return;
    }
    if (item === overtimeLabel) {
      openOvertimeModal();
      return;
    }
    if (item === 'Escala') {
      openSendScheduleModal();
      return;
    }
    if (item === activityDeclarationsLabel) {
      openActivityDeclarationsModal('toIssue');
      return;
    }
    if (item === 'Folhas de Itinerário') {
      openItinerarySheetsModal();
      return;
    }
    if (item === timesheetNoticesLabel) {
      openTimesheetNoticesModal();
      return;
    }
    if (item === 'Folhas de Ponto de vários dias') {
      openTimesheetMultipleDaysModal();
    }
  };

  const handleSettingsToggle =
    (key: (typeof settingsCheckboxItems)[number]['key']) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setSettingsFlags((prev: SettingsFlags) => ({
        ...prev,
        [key]: event.target.checked,
        ...(key === 'alwaysUpdateAfterAssign' && isChecked
          ? { neverUpdate: false }
          : {}),
        ...(key === 'neverUpdate' && isChecked
          ? { alwaysUpdateAfterAssign: false }
          : {}),
      }));
    };

  const handleSettingsItemClick = (item: string) => {
    if (item === emailsScheduleLabel) {
      const modalData: ScheduleEmailsModalData = {
        recipients: ['email@example.com', 'email@example.com', 'email@example.com'],
        ccRecipients: ['email@example.com'],
        isSendingDisabled: false,
      };

      openModal('Emails que recebem a escala', modalData, []);
    }
  };
  

  const [isVisible, setIsVisible] = useState(open);
  const [isAnimatingOpen, setIsAnimatingOpen] = useState(false);

  useEffect(() => {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(
      settingsStorageKey,
      JSON.stringify({
        flags: settingsFlags,
        previewNextDayHours,
        defaultPrinter,
      }),
    );
  }, [settingsFlags, previewNextDayHours, defaultPrinter, settingsStorageKey]);

  useEffect(() => {
    let timeoutId: number | undefined;
    let frameId: number | undefined;
    let frameId2: number | undefined;

    if (open) {
      setIsVisible(true);
      setIsAnimatingOpen(false);
      if (typeof window === 'undefined') {
        setIsAnimatingOpen(true);
        return undefined;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId2 = window.requestAnimationFrame(() => {
          setIsAnimatingOpen(true);
        });
      });
      return () => {
        if (frameId !== undefined) {
          window.cancelAnimationFrame(frameId);
        }
        if (frameId2 !== undefined) {
          window.cancelAnimationFrame(frameId2);
        }
      };
    }

    setIsAnimatingOpen(false);
    timeoutId = window.setTimeout(() => {
      setIsVisible(false);
    }, closeAnimationDurationMs);

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [open, closeAnimationDurationMs]);

  if (!isVisible) {
    return null;
  }

  return (
    <aside
      className={`${styles.drawer} ${isAnimatingOpen ? styles.drawerOpen : styles.drawerClosed}`}
      aria-label="Gerir Escala"
      aria-hidden={!open}
    >
      <div className={styles.header}>
        <span className={styles.title}>Gerir Escala</span>
        <button
          type="button"
          className={`${styles.buttonReset} ${styles.closeButton}`}
          onClick={onClose}
          aria-label="Fechar painel"
        >
          <CloseRoundedIcon className={styles.closeIcon} />
        </button>
      </div>

      <div className={styles.content}>
        <AccordionSection
          id={filterSectionId}
          title="Filtrar"
          isOpen={filtersOpen}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          headerContent={
            <span
              className={styles.filterActions}
              style={{
                opacity: filtersApplied > 0 ? 1 : 0,
                pointerEvents: filtersApplied > 0 ? 'auto' : 'none',
              }}
            >
              <button
                type="button"
                className={`${styles.buttonReset} ${styles.filterClear}`}
                onClick={handleClearFilters}
              >
                Limpar Filtros
              </button>
              <span className={styles.filterBadge}>{filtersApplied}</span>
            </span>
          }
        >
          <div className={styles.checkboxRowRight}>
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={applyDefaultFilters}
                  onChange={(event) =>
                    setApplyDefaultFilters(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Aplicar estes Filtros por defeito"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
          </div>

          <div className={styles.fieldStack}>
            <SelectField
              label="Cliente"
              value={clientFilter}
              options={clientOptions}
              onChange={setClientFilter}
            />
            <SelectField
              label="Requisição"
              value={requestFilter}
              options={requestOptions}
              onChange={setRequestFilter}
            />
            <SelectField
              label="Tipo de Serviço"
              value={serviceTypeFilter}
              options={serviceTypeOptions}
              onChange={setServiceTypeFilter}
            />
            <SelectField
              label="Carregar Serviços"
              value={loadServicesFilter}
              options={loadServicesOptions}
              onChange={setLoadServicesFilter}
            />
            <SelectField
              label="Lotação"
              value={capacityFilter}
              options={capacityOptions}
              onChange={setCapacityFilter}
            />
            <SelectField
              label="Data dos Serviços"
              value={serviceDateFilter}
              options={serviceDateOptions}
              onChange={setServiceDateFilter}
            />
            <SelectField
              label="Origem dos Serviços"
              value={serviceOriginFilter}
              options={serviceOriginOptions}
              onChange={setServiceOriginFilter}
            />
            <SelectField
              label="Destino dos Serviços"
              value={serviceDestinationFilter}
              options={serviceDestinationOptions}
              onChange={setServiceDestinationFilter}
            />
            <SelectField
              label="Motorista"
              value={driverFilter}
              options={driverOptions}
              onChange={setDriverFilter}
            />
            <SelectField
              label="Viatura"
              value={vehicleFilter}
              options={vehicleOptions}
              onChange={setVehicleFilter}
            />
          </div>

          <div className={styles.checkboxStack}>
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={filterWithNotes}
                  onChange={(event) =>
                    setFilterWithNotes(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Com Observações"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={filterWithoutDriver}
                  onChange={(event) =>
                    setFilterWithoutDriver(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Sem Motorista atribuído"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={filterWithoutVehicle}
                  onChange={(event) =>
                    setFilterWithoutVehicle(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Sem Viatura atribuída"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  checked={filterWithAssignments}
                  onChange={(event) =>
                    setFilterWithAssignments(event.target.checked)
                  }
                  size="small"
                  sx={{ padding: '4px' }}
                />
              }
              label="Com Motorista e Viatura atribuídos"
              sx={{
                margin: 0,
                '.MuiTypography-root': {
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  color: '#092056',
                },
              }}
            />
          </div>
        </AccordionSection>

        <AccordionSection
          id={consultSectionId}
          title="Consultar"
          isOpen={consultOpen}
          onToggle={() => setConsultOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {consultItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleConsultItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={sendSectionId}
          title="Enviar"
          isOpen={sendOpen}
          onToggle={() => setSendOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {sendItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleSendItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={printSectionId}
          title="Imprimir"
          isOpen={printOpen}
          onToggle={() => setPrintOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {printItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handlePrintItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={exportSectionId}
          title="Exportar"
          isOpen={exportOpen}
          onToggle={() => setExportOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.consultList}>
            {exportItems.map((item) => (
              <button
                key={item}
                type="button"
                className={styles.consultItem}
                onClick={() => handleExportItemClick(item)}
              >
                <span className={styles.consultText}>{item}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection
          id={settingsSectionId}
          title="Definições"
          isOpen={settingsOpen}
          onToggle={() => setSettingsOpen((prev) => !prev)}
          className={styles.sectionCompact}
        >
          <div className={styles.settingsStack}>
            <div className={styles.checkboxStack}>
              {settingsCheckboxItems.map((item) => (
                <FormControlLabel
                  key={item.key}
                  className={styles.checkboxLabel}
                  control={
                    <Checkbox
                      checked={settingsFlags[item.key]}
                      onChange={handleSettingsToggle(item.key)}
                      size="small"
                      sx={{ padding: '4px' }}
                    />
                  }
                  label={item.label}
                  sx={{
                    margin: 0,
                    '.MuiTypography-root': {
                      fontFamily: 'Poppins',
                      fontSize: '13px',
                      fontWeight: 500,
                      lineHeight: 1.3,
                      color: '#092056',
                    },
                  }}
                />
              ))}
            </div>

            <div className={styles.fieldStack}>
              <SelectField
                label="Antever a escala do dia seguinte"
                value={previewNextDayHours}
                options={['0 horas']}
                onChange={setPreviewNextDayHours}
              />
              <SelectField
                label="Impressora pre-definida"
                value={defaultPrinter}
                options={['<Nome da Impressora>']}
                onChange={setDefaultPrinter}
              />
            </div>
            <div className={styles.consultList}>
              <button
                type="button"
                className={styles.consultItem}
                onClick={() => handleSettingsItemClick(emailsScheduleLabel)}
              >
                <span className={styles.consultText}>{emailsScheduleLabel}</span>
                <span className={styles.consultIcon}>
                  <ChevronRightIcon fontSize="inherit" />
                </span>
              </button>
            </div>
          </div>
        </AccordionSection>
      </div>
    </aside>
  );
};

export default VehicleManagementDrawer;









